import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { AffilmasterApiError, getAffilmasterVipOverview, runAffilmasterVipSync, setAffilmasterVipCredentials, type VipAccount, type VipInstance, type VipOverview } from '../api/affilmaster';

const CLASSIFICATION: Record<string, string> = {
  explicit_paid_assignment: 'Konkrétní placené přiřazení',
  default_with_commission_history: 'Výchozí sazba s historií provizí',
  historical_nonzero_current_zero: 'Historické provize, nyní 0 %',
  unconfirmed_default: 'Nepotvrzený výchozí profil',
  not_in_campaign: 'Mimo kampaň',
  unverified: 'Neověřeno',
};
const MEMBERSHIP: Record<string, string> = { A: 'Schválené', R: 'Ascending', F: 'Descending', X: 'Fixní' };
const ACCESS: Record<string, { label: string; cls: string }> = {
  ok: { label: 'Čte se', cls: 'badge-soft-success' },
  pending: { label: 'Přístup zadán, čeká na čtení', cls: 'badge-soft-warning' },
  access_missing: { label: 'Přístup chybí', cls: 'badge-soft-warning' },
  auth_rejected: { label: 'Přihlášení odmítnuto', cls: 'badge-soft-danger' },
  deferred: { label: 'Odloženo (limit)', cls: 'badge-soft-warning' },
  source_read_failed: { label: 'Čtení selhalo', cls: 'badge-soft-danger' },
};

function pct(v: number | null): string {
  if (v == null) return '-';
  return `${(v * 100).toLocaleString('cs-CZ', { maximumFractionDigits: 3 })} %`;
}
function czDate(v: string | null): string {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function primaryWebsite(raw: string | null): string | null {
  if (!raw) return null;
  const first = raw.split('|')[0]?.trim() ?? '';
  const url = first.replace(/^[a-z]+:(?=https?:\/\/)/i, '');
  return url ? url.replace(/^https?:\/\//, '').replace(/\/$/, '') : null;
}
function pairingNote(a: VipAccount): string | null {
  if (a.note) return a.note;
  if (a.companyInSource && a.cardName && a.companyInSource.trim().toLowerCase() !== a.cardName.trim().toLowerCase()) return `Ve zdroji firma „${a.companyInSource}“.`;
  return null;
}

export function VipPartnersSettings({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<VipOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showGroups, setShowGroups] = useState(false);
  const [credFor, setCredFor] = useState<VipInstance | null>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const pollUntil = useRef<number>(0);

  const load = useCallback(() => getAffilmasterVipOverview().then(setData).catch((e: Error) => setError(e?.message || 'Chyba načítání')), []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!data?.syncRunning && Date.now() > pollUntil.current) return;
    const t = window.setTimeout(() => { void load(); }, 8000);
    return () => window.clearTimeout(t);
  }, [data, load]);

  async function startSync() {
    setSyncBusy(true); setSyncMsg(null);
    try {
      await runAffilmasterVipSync();
      pollUntil.current = Date.now() + 15 * 60 * 1000;
      setSyncMsg('Načítání z instancí běží na pozadí. Stav se tu průběžně obnovuje, hotové instance mají ve sloupci Přístup „Čte se“ a vyplněné poslední čtení.');
      window.setTimeout(() => { void load(); }, 3000);
    } catch (e) {
      setSyncMsg(e instanceof AffilmasterApiError ? e.message : 'Spuštění se nepovedlo');
    } finally { setSyncBusy(false); }
  }

  const inventory = data?.sync.find((s) => s.source === 'vip_pap_inventory') ?? null;
  const stats = data?.sync.find((s) => s.source === 'vip_pap_stats') ?? null;

  return (
    <>
      <button type="button" className="btn btn-sm btn-link px-0 mb-2" onClick={onBack}>Zpět na Nastavení</button>
      <div className="d-flex align-items-center mb-1" style={{ gap: 12 }}>
        <h5 className="mb-0">VIP affiliate partneři - Affilmaster</h5>
      </div>
      <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
        Partneři s vlastním účtem přímo v PaP instanci kasina (mimo centrální Affilmaster PaP). Přehled napojení, provizních
        podmínek a započtení do ekonomiky klientů. Údaje se čtou ze zdrojových instancí každou noc; přístupy k instancím zadáš
        níže v tabulce Instance kasin tlačítkem Nastavit přístup.
      </p>
      {data && (
        <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 10 }}>
          <button type="button" className="btn btn-sm btn-primary" disabled={syncBusy || data.syncRunning || data.instances.every((i) => i.credentialsSource === 'none')} onClick={() => void startSync()}>
            {data.syncRunning ? 'Načítání běží…' : 'Načíst data z instancí teď'}
          </button>
          {syncMsg && <span className="text-muted" style={{ fontSize: '0.85rem' }}>{syncMsg}</span>}
          {!syncMsg && data.syncRunning && <span className="text-muted" style={{ fontSize: '0.85rem' }}>Načítání z instancí právě běží.</span>}
        </div>
      )}

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {!data && !error && <div className="text-muted py-3">Načítám…</div>}

      {data && (
        <>
          <div className="card mb-3"><div className="card-body py-2">
            <div className="d-flex flex-wrap" style={{ gap: 18, fontSize: '0.88rem' }}>
              <span><strong>Partneři:</strong> {data.partners.length}</span>
              <span><strong>Účty v instancích:</strong> {data.partners.reduce((n, p) => n + p.accounts.length, 0)}</span>
              <span><strong>Instance:</strong> {data.instances.length}, čtou se {data.instances.filter((i) => i.accessStatus === 'ok').length}</span>
              <span><strong>Poslední čtení podmínek:</strong> {czDate(inventory?.lastSuccessAt ?? null)}{inventory?.status && inventory.status !== 'ok' ? ` (${inventory.status})` : ''}</span>
              <span><strong>Statistiky do:</strong> {stats?.dataThroughDate ?? '-'}{stats?.status && stats.status !== 'ok' ? ` (${stats.status})` : ''}</span>
            </div>
          </div></div>

          {data.partners.map((p) => (
            <div className="card mb-3" key={p.papAffiliateId}>
              <div className="card-body pb-2">
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                  <h6 className="mb-0">{p.name ?? p.papAffiliateId}</h6>
                  <code style={{ fontSize: '0.78rem' }}>{p.papAffiliateId}</code>
                  {primaryWebsite(p.website) && <a href={`https://${primaryWebsite(p.website)}`} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem' }}>{primaryWebsite(p.website)}</a>}
                  <span className="badge badge-soft-success ms-auto">{p.accounts.length} {p.accounts.length === 1 ? 'instance' : p.accounts.length < 5 ? 'instance' : 'instancí'}</span>
                </div>
              </div>
              <div className="table-responsive-mobile">
                <table className="table table-sm mb-0 align-middle">
                  <thead><tr><th>Kasino</th><th>Účet ve zdroji</th><th>Referral ID</th><th>Kampaň</th><th>Provizní skupina</th><th className="text-end">per Sale</th><th>Členství</th><th>Zařazení</th><th>Účtovat klientovi</th><th>Čteno</th></tr></thead>
                  <tbody>
                    {p.accounts.map((a) => {
                      const note = pairingNote(a);
                      return (
                        <tr key={a.id}>
                          <td><strong>{a.casino}</strong></td>
                          <td>
                            <div>{a.sourceName ?? '-'}</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>ID <code>{a.localAffiliateId}</code>{a.websiteInSource ? ` · ${a.websiteInSource.replace(/^https?:\/\//, '').replace(/\/$/, '')}` : ''}</div>
                            {note && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{note}</div>}
                          </td>
                          <td><code>{a.referralId ?? '-'}</code></td>
                          <td><code>{a.campaignId}</code></td>
                          <td>{a.commissionGroupName ?? '-'}{a.commissionGroupId && <div className="text-muted" style={{ fontSize: '0.75rem' }}><code>{a.commissionGroupId}</code></div>}{a.otherCommissions && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{a.otherCommissions}</div>}</td>
                          <td className="text-end"><strong>{pct(a.perSaleRate)}</strong></td>
                          <td>{a.membershipStatus ? `${MEMBERSHIP[a.membershipStatus] ?? a.membershipStatus} (${a.membershipStatus})` : a.assignmentKind === 'inherited_default' ? 'Výchozí (zděděné)' : '-'}</td>
                          <td style={{ fontSize: '0.82rem' }}>{CLASSIFICATION[a.classification ?? ''] ?? a.classification ?? '-'}</td>
                          <td>{a.vipBilling ? <span className="badge badge-soft-success">Ano</span> : <span className="badge badge-soft-secondary">Ne</span>}</td>
                          <td className="text-muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{czDate(a.sourceObservedAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="card mb-3"><div className="card-body pb-2"><h6 className="mb-0">Instance kasin</h6></div>
            <div className="table-responsive-mobile">
              <table className="table table-sm mb-0 align-middle">
                <thead><tr><th>Kasino</th><th>Adresa instance</th><th>Hlavní kampaň</th><th>Klient v P1</th><th>Účtovat i VIP</th><th>Účty</th><th>Přístup</th><th>Poslední čtení</th><th></th></tr></thead>
                <tbody>
                  {data.instances.map((i) => {
                    const acc = ACCESS[i.accessStatus] ?? { label: i.accessStatus, cls: 'badge-soft-secondary' };
                    return (
                      <tr key={i.id}>
                        <td><strong>{i.casino}</strong></td>
                        <td><a href={i.origin} target="_blank" rel="noreferrer">{i.origin.replace(/^https?:\/\//, '')}</a></td>
                        <td><code>{i.mainCampaignId}</code></td>
                        <td>{i.partnerName ?? <span className="text-muted">nenavázáno</span>}</td>
                        <td>{i.vipBilling ? <span className="badge badge-soft-success">Ano</span> : <span className="badge badge-soft-secondary">Ne</span>}</td>
                        <td>{i.accounts}</td>
                        <td>
                          <span className={`badge ${acc.cls}`} title={i.lastError ?? undefined}>{acc.label}</span>
                          {i.credentialsSource === 'stored' && <div className="text-muted" style={{ fontSize: '0.72rem' }}>účet {i.credentialsUser}</div>}
                          {i.credentialsSource === 'env' && <div className="text-muted" style={{ fontSize: '0.72rem' }}>přístup ze serverové konfigurace</div>}
                          {i.lastError && i.accessStatus !== 'ok' && <div className="text-danger" style={{ fontSize: '0.72rem' }}>{i.lastError}</div>}
                        </td>
                        <td className="text-muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{czDate(i.lastReadAt)}</td>
                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                          {i.credentialsSource !== 'env' && (
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!data.canStoreCredentials} title={data.canStoreCredentials ? undefined : 'Ukládání přístupů není na serveru dostupné'} onClick={() => setCredFor(i)}>
                              {i.credentialsSource === 'stored' ? 'Změnit přístup' : 'Nastavit přístup'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body pb-2 d-flex align-items-center" style={{ gap: 12 }}>
              <h6 className="mb-0">Provizní skupiny v instancích</h6>
              <span className="text-muted" style={{ fontSize: '0.82rem' }}>{data.groups.length ? `${data.groups.length} skupin z posledního čtení` : 'Načtou se při prvním čtení instancí'}</span>
              {data.groups.length > 0 && <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => setShowGroups((v) => !v)}>{showGroups ? 'Skrýt' : 'Zobrazit'}</button>}
            </div>
            {showGroups && (
              <div className="table-responsive-mobile">
                <table className="table table-sm mb-0 align-middle">
                  <thead><tr><th>Kasino</th><th>Kampaň</th><th>Skupina</th><th className="text-end">per Sale</th><th>Nastavení provizí</th><th className="text-end">Explicitních členů</th></tr></thead>
                  <tbody>
                    {data.groups.map((g) => (
                      <tr key={`${g.instanceCode}|${g.campaignId}|${g.commissionGroupId}`}>
                        <td>{g.casino}</td>
                        <td>{g.campaignName ?? ''} <code style={{ fontSize: '0.75rem' }}>{g.campaignId}</code></td>
                        <td>{g.name ?? '-'}{g.isDefault && <span className="badge badge-soft-secondary ms-1">výchozí</span>} <code style={{ fontSize: '0.75rem' }}>{g.commissionGroupId}</code></td>
                        <td className="text-end">{pct(g.perSaleRate)}</td>
                        <td className="text-muted" style={{ fontSize: '0.78rem' }}>{g.commissionSummary ?? '-'}</td>
                        <td className="text-end">{g.explicitMembers ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      {credFor && (
        <CredentialsModal
          instance={credFor}
          onClose={() => setCredFor(null)}
          onSaved={() => { setCredFor(null); void load(); }}
        />
      )}
    </>
  );
}

function CredentialsModal({ instance, onClose, onSaved }: { instance: VipInstance; onClose: () => void; onSaved: () => void }) {
  const [user, setUser] = useState(instance.credentialsUser ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (user.trim().length < 2) { setErr('Zadej uživatelské jméno merchant účtu'); return; }
    if (password.length < 4) { setErr('Zadej heslo merchant účtu'); return; }
    setBusy(true); setErr(null);
    try { await setAffilmasterVipCredentials(instance.id, user.trim(), password); onSaved(); }
    catch (e2) { setErr(e2 instanceof AffilmasterApiError ? e2.message : 'Uložení se nepovedlo'); }
    finally { setBusy(false); }
  }
  async function clear() {
    setBusy(true); setErr(null);
    try { await setAffilmasterVipCredentials(instance.id, '', ''); onSaved(); }
    catch (e2) { setErr(e2 instanceof AffilmasterApiError ? e2.message : 'Odebrání se nepovedlo'); }
    finally { setBusy(false); }
  }
  return (
    <div className="prototype-modal-backdrop" onClick={() => !busy && onClose()}>
      <form className="card prototype-modal-card" style={{ width: '100%', maxWidth: 480 }} onSubmit={(e) => void submit(e)} onClick={(e) => e.stopPropagation()} autoComplete="off">
        <div className="prototype-modal-head"><div><div className="prototype-eyebrow">PŘÍSTUP K INSTANCI</div><h2>{instance.casino}</h2></div><button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={onClose}>×</button></div>
        <div className="card-body">
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            Merchant přihlášení do <a href={instance.origin} target="_blank" rel="noreferrer">{instance.origin.replace(/^https?:\/\//, '')}</a>. Heslo se uloží šifrované na serveru a nikde se nezobrazuje; používá ho jen noční čtení dat.
          </p>
          {err && <div className="alert alert-danger py-2">{err}</div>}
          <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Uživatelské jméno</label>
          <input className="form-control form-control-sm mb-2" value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" />
          <label className="form-label mb-1" style={{ fontSize: '0.85rem' }}>Heslo</label>
          <input className="form-control form-control-sm" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder={instance.credentialsSource === 'stored' ? 'Nové heslo (stávající zůstává skryté)' : ''} />
        </div>
        <div className="d-flex px-3 pb-3" style={{ gap: 8 }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Ukládám…' : 'Uložit přístup'}</button>
          {instance.credentialsSource === 'stored' && <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => void clear()}>Odebrat přístup</button>}
          <button type="button" className="btn btn-link btn-sm ms-auto" disabled={busy} onClick={onClose}>Zavřít</button>
        </div>
      </form>
    </div>
  );
}
